import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, MapPin, Stethoscope, Phone, Calendar, Shield, Users, Star, Clock, CheckCircle, ArrowRight, ArrowLeft, Heart, Eye, Brain, Baby, Bone, Smile, Calculator, Syringe, Activity, Pill, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import DoctorCard from '../components/DoctorCard';
import AlgeriaMap from '../components/AlgeriaMap';
import Navbar from '../components/Navbar';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const HERO_IMAGE = "/hero.jpg";

// Spécialités populaires avec raccourcis rapides
const QUICK_SPECIALTIES = [
  { id: 'general', label: 'Généraliste', icon: '🩺' },
  { id: 'cardiology', label: 'Cardiologue', icon: '❤️' },
  { id: 'dermatology', label: 'Dermatologue', icon: '🧴' },
  { id: 'pediatrics', label: 'Pédiatre', icon: '👶' },
  { id: 'gynecology', label: 'Gynécologue', icon: '🌸' },
  { id: 'ophthalmology', label: 'Ophtalmologue', icon: '👁️' },
  { id: 'dentistry', label: 'Dentiste', icon: '🦷' },
  { id: 'orthopedics', label: 'Orthopédiste', icon: '🦴' },
  // Pas des spécialités médicales mais des types de fiche (labo d'analyses,
  // clinique) — demandé par Nadjib pour retrouver sur l'accueil les mêmes
  // raccourcis que les boutons "Laboratoires" / "Cliniques" de /search.
  // `entityType: true` les distingue d'une vraie spécialité dans
  // handleQuickSpecialty ci-dessous. Ces "Cliniques" sont les 133 fiches
  // établissement importées de doctofree (entity_type=Clinique), pas les
  // vraies cliniques inscrites qui ont déjà leur propre section sur /search.
  { id: 'Laboratoire', label: 'Laboratoires', icon: '🔬', entityType: true },
  { id: 'Clinique', label: 'Cliniques', icon: '🏥', entityType: true },
  // Pas un raccourci /search comme les autres : renvoie directement vers
  // l'annuaire des pharmacies (/pharmacies, distinct de la recherche
  // medecins) via `directLink`, gere a part dans handleQuickSpecialty.
  // Ajoute suite au constat qu'aucun lien de l'accueil ne menait a cette
  // page (Nadjib, 2026-09-05).
  { id: 'pharmacies', label: 'Pharmacies', icon: '💊', directLink: '/pharmacies' },
];

// Témoignages patients
const HEALTH_TOOLS = [
  {
    icon: <Pill className="w-6 h-6 text-[#0596DE]" />,
    title: 'Posologie pédiatrique',
    desc: 'Dose de paracétamol et d\'ibuprofène pour votre enfant, selon son poids.',
    to: '/outils-sante/posologie-enfant',
  },
  {
    icon: <Calculator className="w-6 h-6 text-[#0596DE]" />,
    title: 'Calculateur d\'IMC',
    desc: 'Votre indice de masse corporelle et votre catégorie de poids.',
    to: '/outils-sante/imc',
  },
  {
    icon: <Heart className="w-6 h-6 text-[#0596DE]" />,
    title: 'Calculateur d\'ovulation',
    desc: 'Votre période de fertilité et la date de vos prochaines règles.',
    to: '/outils-sante/ovulation',
  },
  {
    icon: <Baby className="w-6 h-6 text-[#0596DE]" />,
    title: 'Suivi de grossesse',
    desc: 'Votre date d\'accouchement et l\'avancement de votre grossesse.',
    to: '/outils-sante/grossesse',
  },
  {
    icon: <Syringe className="w-6 h-6 text-[#0596DE]" />,
    title: 'Calendrier vaccinal',
    desc: 'Le calendrier de vaccination de l\'enfant en Algérie.',
    to: '/outils-sante/vaccination',
  },
  {
    icon: <Activity className="w-6 h-6 text-[#0596DE]" />,
    title: 'Risque cardiovasculaire',
    desc: 'Un premier repère sur vos facteurs de risque cardiovasculaire.',
    to: '/outils-sante/risque-cardiovasculaire',
  },
];

const TESTIMONIALS = [
  {
    name: 'Amina B.',
    city: 'Tizi Ouzou',
    text: 'J\'ai trouvé un cardiologue disponible le lendemain même. Avant je devais attendre 3 semaines en appelant. Tabbibak a changé ma façon de voir les RDV médicaux.',
    rating: 5,
    specialty: 'Cardiologie',
  },
  {
    name: 'Karim M.',
    city: 'Alger',
    text: 'Interface simple, RDV confirmé en 2 minutes. Le médecin a été notifié automatiquement. Je recommande à toute ma famille.',
    rating: 5,
    specialty: 'Médecine générale',
  },
  {
    name: 'Yasmine L.',
    city: 'Oran',
    text: 'Enfin une solution algérienne qui fonctionne vraiment ! J\'ai pris RDV pour mes enfants chez un pédiatre sans aucun appel téléphonique.',
    rating: 5,
    specialty: 'Pédiatrie',
  },
];

const Home = () => {
  const navigate = useNavigate();
  const { t, isRTL } = useLanguage();
  const [specialty, setSpecialty] = useState('');
  const [wilaya, setWilaya] = useState('');
  const [daira, setDaira] = useState('');
  const [searchName, setSearchName] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [wilayas, setWilayas] = useState([]);
  const [dairas, setDairas] = useState([]);
  const [featuredDoctors, setFeaturedDoctors] = useState([]);
  const [stats, setStats] = useState({ doctors: 0, patients: 0 });
  const [blogArticles, setBlogArticles] = useState([]);

  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [specRes, wilRes, docRes, blogRes] = await Promise.all([
          axios.get(`${API_URL}/api/specialties`),
          axios.get(`${API_URL}/api/wilayas`),
          axios.get(`${API_URL}/api/doctors?limit=12&sort_by=rating`),
          axios.get(`${API_URL}/api/articles?limit=3`)
        ]);
        setSpecialties(specRes.data);
        setWilayas(wilRes.data);
        // ✅ Filtrer uniquement les médecins avec un vrai nom
        const docs = (docRes.data.doctors || []).filter(d => d.name && d.name !== '-' && d.name.length > 2);
        setFeaturedDoctors(docs.slice(0, 6));
        setStats({ doctors: docRes.data.total || 0, patients: 5200 });
        setBlogArticles(blogRes.data.articles || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (!wilaya || wilaya === 'all') { setDairas([]); setDaira(''); return; }
    axios.get(`${API_URL}/api/wilayas/${wilaya}/dairas`)
      .then(r => setDairas(r.data))
      .catch(() => setDairas([]));
    setDaira('');
  }, [wilaya]);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (specialty && specialty !== 'all') params.set('specialty', specialty);
    if (wilaya && wilaya !== 'all') params.set('wilaya', wilaya);
    if (daira && daira !== 'all') params.set('daira', daira);
    if (searchName) params.set('name', searchName);
    navigate(`/search?${params.toString()}`);
  };

  const handleQuickSpecialty = (s) => {
    if (s.directLink) { navigate(s.directLink); return; }
    navigate(s.entityType ? `/search?entity_type=${s.id}` : `/search?specialty=${s.id}`);
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative overflow-hidden min-h-[680px]" data-testid="hero-section">
        <div className="absolute inset-0 bg-gradient-to-br from-[#020B18] via-[#021525] to-[#0596DE]/30 z-0" />
        <div className="absolute inset-0 z-0" style={{background: "radial-gradient(ellipse at 70% 50%, rgba(5,150,222,0.15) 0%, transparent 60%)"}} />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-0 items-center min-h-[600px] py-12 lg:py-0">

            {/* Text + Search */}
            <div className="space-y-8 max-w-xl">
              <div className="space-y-5">
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full">
                  <span className="w-2 h-2 rounded-full bg-[#10B981] animate-pulse" />
                  <span className="text-sm font-medium text-white">{stats.doctors.toLocaleString()}+ {t('hero_badge')}</span>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-bold text-white leading-[1.1] tracking-tight">
                  {t('hero_title_1')}<span className="text-[#0596DE] relative">
                    {t('hero_title_highlight')}
                    <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 200 12" fill="none"><path d="M2 8C50 2 150 2 198 8" stroke="#0596DE" strokeWidth="3" strokeLinecap="round" opacity="0.3"/></svg>
                  </span>
                </h1>
                <p className="text-lg text-white/70 leading-relaxed">
                  {t('hero_subtitle')}
                </p>
              </div>

              {/* Search Bar */}
              <form
                onSubmit={handleSearch}
                className="bg-white rounded-2xl shadow-[0_4px_40px_rgba(5,150,222,0.12)] border border-[#E2E8F0] p-4"
                data-testid="search-form"
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                  <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-xl px-3 py-2.5">
                    <Stethoscope className="w-4 h-4 text-[#0596DE] flex-shrink-0" />
                    <Select value={specialty} onValueChange={setSpecialty}>
                      <SelectTrigger className="border-0 shadow-none focus:ring-0 p-0 h-auto bg-transparent text-sm" data-testid="specialty-select">
                        <SelectValue placeholder={t('search_specialty')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('search_all_specialties')}</SelectItem>
                        {specialties.map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name_fr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-xl px-3 py-2.5">
                    <MapPin className="w-4 h-4 text-[#0596DE] flex-shrink-0" />
                    <Select value={wilaya} onValueChange={setWilaya}>
                      <SelectTrigger className="border-0 shadow-none focus:ring-0 p-0 h-auto bg-transparent text-sm" data-testid="wilaya-select">
                        <SelectValue placeholder={t('search_wilaya')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('search_all_wilayas')}</SelectItem>
                        {wilayas.map((w) => (
                          <SelectItem key={w.code} value={w.code}>{w.code} - {w.name_fr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-xl px-3 py-2.5">
                    <MapPin className="w-4 h-4 text-[#10B981] flex-shrink-0" />
                    <Select value={daira} onValueChange={setDaira} disabled={!wilaya || wilaya === 'all'}>
                      <SelectTrigger className="border-0 shadow-none focus:ring-0 p-0 h-auto bg-transparent text-sm" data-testid="daira-select">
                        <SelectValue placeholder={t('search_daira')} />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">{t('search_all_dairas')}</SelectItem>
                        {dairas.map((d) => (
                          <SelectItem key={d.name_fr} value={d.name_fr}>{d.name_fr}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-2 bg-[#F8FAFC] rounded-xl px-3 py-2.5">
                    <Search className="w-4 h-4 text-[#0596DE] flex-shrink-0" />
                    <Input
                      type="text"
                      placeholder={t('search_doctor_name')}
                      value={searchName}
                      onChange={(e) => setSearchName(e.target.value)}
                      className="border-0 shadow-none focus-visible:ring-0 p-0 h-auto bg-transparent text-sm"
                      data-testid="search-name-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  className="w-full rounded-xl bg-[#0596DE] hover:bg-[#047CBA] h-12 text-base font-semibold transition-all hover:shadow-lg hover:shadow-[#0596DE]/20"
                  data-testid="search-submit-btn"
                >
                  <Search className="w-5 h-5 mr-2" />
                  {t('search_btn')}
                </Button>
              </form>

              {/* ✅ NOUVEAU - Raccourcis spécialités populaires */}
              <div>
                <p className="text-xs text-white/50 mb-3 font-medium uppercase tracking-wider">Spécialités populaires</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_SPECIALTIES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => handleQuickSpecialty(s)}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 border border-white/20 rounded-full text-sm text-white/80 hover:border-[#0596DE] hover:text-white hover:bg-[#0596DE]/30 transition-all backdrop-blur-sm"
                    >
                      <span>{s.icon}</span>
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Vous gerez un laboratoire ? Inscription dediee (distincte des cliniques). */}
              <a
                href="/laboratoire/inscription"
                className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white underline underline-offset-2"
              >
                🔬 Vous gérez un laboratoire ? Inscrivez-le gratuitement
              </a>

              {/* Trust indicators */}
              <div className="flex items-center gap-6 text-sm text-white/60">
                <span className="flex items-center gap-1.5"><CheckCircle className="w-4 h-4 text-[#10B981]" />{t('hero_free')}</span>
                <span className="flex items-center gap-1.5"><Clock className="w-4 h-4 text-[#0596DE]" />{t('hero_24h')}</span>
                <span className="flex items-center gap-1.5"><Shield className="w-4 h-4 text-[#F59E0B]" />{t('hero_verified')}</span>
              </div>
            </div>

            {/* Doctor Image */}
            <div className="relative flex justify-center lg:justify-end items-end">
              <div className="relative">
                {/* Carte Algérie */}
                <div className="absolute -right-32 -top-10 w-80 h-80 opacity-60 pointer-events-none z-0 hidden lg:block">
                  <AlgeriaMap />
                </div>
                <div className="absolute -top-8 -left-8 w-72 h-72 bg-[#0596DE]/10 rounded-full blur-3xl" />
                <div className="absolute -bottom-4 -right-4 w-48 h-48 bg-[#10B981]/5 rounded-full blur-2xl" />

                <div className="relative z-10">
                  <img
                    src={HERO_IMAGE}
                    alt="Tabbibak.dz"
                    width={480}
                    height={560}
                    fetchpriority="high"
                    className="w-[280px] h-[350px] sm:w-[360px] sm:h-[440px] lg:w-[480px] lg:h-[560px] object-cover object-top rounded-3xl shadow-2xl"
                    data-testid="hero-image"
                  />
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-t from-[#0F172A]/20 via-transparent to-transparent" />
                </div>

                {/* Floating card - Appointment */}
                <div className="absolute -left-4 lg:-left-12 top-16 lg:top-24 bg-white rounded-2xl shadow-xl border border-[#E2E8F0] p-3 lg:p-4 z-20 animate-float">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-[#D1FAE5] flex items-center justify-center">
                      <Calendar className="w-4 h-4 lg:w-5 lg:h-5 text-[#10B981]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B]">{t('hero_next_rdv')}</p>
                      <p className="text-xs lg:text-sm font-semibold text-[#0F172A]">{t('hero_today')}</p>
                    </div>
                  </div>
                </div>

                {/* Floating card - Rating */}
                <div className="absolute -right-2 lg:-right-8 bottom-20 lg:bottom-32 bg-white rounded-2xl shadow-xl border border-[#E2E8F0] p-3 lg:p-4 z-20 animate-float-delayed">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 lg:w-10 lg:h-10 rounded-xl bg-[#FEF3C7] flex items-center justify-center">
                      <Star className="w-4 h-4 lg:w-5 lg:h-5 text-[#F59E0B] fill-[#F59E0B]" />
                    </div>
                    <div>
                      <p className="text-xs text-[#64748B]">{t('hero_reviews')}</p>
                      <div className="flex items-center gap-1">
                        <span className="text-xs lg:text-sm font-semibold text-[#0F172A]">4.8/5</span>
                        <span className="text-xs text-[#64748B]">{t('hero_excellent')}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ✅ Stats Bar corrigées */}
      <section className="bg-[#020B18] border-y border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold text-[#0596DE] font-['Outfit']">69</div>
              <div className="text-sm text-[#64748B] mt-1">{t('stats_wilayas')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#0596DE] font-['Outfit']">{stats.doctors > 1000 ? `${Math.floor(stats.doctors / 1000)}K+` : `${stats.doctors}+`}</div>
              <div className="text-sm text-[#64748B] mt-1">{t('stats_doctors')}</div>
            </div>
            <div className="text-center">
              {/* Correctif : "22" etait fige en dur et ne refletait plus le
                  nombre reel de specialites (des specialites ont ete ajoutees
                  au fil des imports depuis) - on branche desormais sur la
                  liste /api/specialties, deja chargee pour le selecteur de
                  recherche, pour que ce chiffre ne se re-perime plus jamais. */}
              <div className="text-3xl font-bold text-[#0596DE] font-['Outfit']">{specialties.length || 22}</div>
              <div className="text-sm text-[#64748B] mt-1">{t('stats_specialties')}</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-[#0596DE] font-['Outfit']">24/7</div>
              <div className="text-sm text-[#64748B] mt-1">{t('stats_available')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-[#020B18]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-white">{t('how_title')}</h2>
            <p className="text-white/50 mt-3 max-w-lg mx-auto">{t('how_subtitle')}</p>
          </div>
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="relative h-[400px] hidden lg:block">
              <div className="absolute inset-0 bg-[#0596DE]/5 rounded-3xl border border-[#0596DE]/20" />
              <div className="absolute inset-4 opacity-80"><AlgeriaMap /></div>
              <div className="absolute bottom-6 left-6 bg-white/10 backdrop-blur-md rounded-2xl p-4 border border-white/20"><div className="text-white font-bold text-2xl">69</div><div className="text-white/60 text-sm">Wilayas couvertes</div></div>
              {/* Correctif : "18K+" etait fige en dur, desynchronise du vrai
                  total (deja recupere dans `stats.doctors` pour le badge du
                  hero et la barre de stats juste en dessous) - meme formule
                  reutilisee ici pour ne plus jamais afficher un chiffre perime. */}
              <div className="absolute top-6 right-6 bg-[#0596DE]/20 backdrop-blur-md rounded-2xl p-4 border border-[#0596DE]/30"><div className="text-[#0596DE] font-bold text-2xl">{stats.doctors > 1000 ? `${Math.floor(stats.doctors / 1000)}K+` : `${stats.doctors}+`}</div><div className="text-white/60 text-sm">Médecins</div></div>
            </div>
            <div className="space-y-6">
              {[
                { step: "1", icon: Search, color: "#0596DE", bg: "rgba(5,150,222,0.15)", title: t("how_step1_title"), desc: t("how_step1_desc") },
                { step: "2", icon: Calendar, color: "#10B981", bg: "rgba(16,185,129,0.15)", title: t("how_step2_title"), desc: t("how_step2_desc") },
                { step: "3", icon: CheckCircle, color: "#F59E0B", bg: "rgba(245,158,11,0.15)", title: t("how_step3_title"), desc: t("how_step3_desc") },
              ].map(({ step, icon: Icon, color, bg, title, desc }) => (
                <div key={step} className="flex items-start gap-4 p-4 rounded-2xl border border-white/10 bg-white/5 hover:border-[#0596DE]/40 transition-all">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: bg }}><Icon className="w-6 h-6" style={{ color }} /></div>
                  <div><div className="text-xs font-bold mb-1" style={{ color }}>ÉTAPE {step}</div><h3 className="text-white font-semibold mb-1">{title}</h3><p className="text-white/50 text-sm">{desc}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="hidden grid md:grid-cols-3 gap-8 relative">
            <div className="hidden md:block absolute top-16 left-1/6 right-1/6 h-0.5 bg-gradient-to-r from-[#0596DE]/20 via-[#0596DE]/40 to-[#0596DE]/20" />

            {[
              { step: '1', icon: Search, color: '#0596DE', bg: '#E6F4FB', title: t('how_step1_title'), desc: t('how_step1_desc') },
              { step: '2', icon: Calendar, color: '#10B981', bg: '#D1FAE5', title: t('how_step2_title'), desc: t('how_step2_desc') },
              { step: '3', icon: CheckCircle, color: '#F59E0B', bg: '#FEF3C7', title: t('how_step3_title'), desc: t('how_step3_desc') },
            ].map(({ step, icon: Icon, color, bg, title, desc }) => (
              <div key={step} className="relative text-center group">
                <div className="relative z-10 mb-6">
                  <div className="w-20 h-20 rounded-2xl flex items-center justify-center mx-auto transition-transform group-hover:scale-110 group-hover:shadow-lg" style={{ backgroundColor: bg }}>
                    <Icon className="w-9 h-9" style={{ color }} />
                  </div>
                  <span className="absolute -top-2 w-8 h-8 rounded-full bg-[#0596DE] text-white text-sm font-bold flex items-center justify-center shadow-lg" style={{ [isRTL ? 'left' : 'right']: 'calc(50% - 52px)' }}>
                    {step}
                  </span>
                </div>
                <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{title}</h3>
                <p className="text-[#64748B] text-sm leading-relaxed max-w-xs mx-auto">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ NOUVEAU - Section Témoignages */}
      <section className="py-20 bg-[#021525]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-white">Ce que disent nos patients</h2>
            <p className="text-white/60 mt-3">Des milliers d'Algériens font confiance à Tabbibak chaque jour</p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 hover:border-[#0596DE]/50 transition-all">
                <div className="flex items-center gap-1 mb-4">
                  {[1,2,3,4,5].map((s) => (
                    <Star key={s} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />
                  ))}
                </div>
                <p className="text-white/70 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#0596DE]/30 flex items-center justify-center">
                      <span className="text-sm font-bold text-[#0596DE]">{t.name[0]}</span>
                    </div>
                    <div>
                      <p className="font-semibold text-white text-sm">{t.name}</p>
                      <p className="text-xs text-white/50">{t.city}</p>
                    </div>
                  </div>
                  <span className="text-xs bg-[#0596DE]/20 text-[#0596DE] px-2 py-1 rounded-full border border-[#0596DE]/30">{t.specialty}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ✅ NOUVEAU - Section contenu : présentation + spécialités */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
              Prendre rendez-vous en ligne avec un médecin en Algérie
            </h2>
            <p className="text-[#64748B] mt-4 leading-relaxed">
              Tabbibak.com est la plateforme algérienne qui vous permet de trouver un médecin et
              de prendre rendez-vous en ligne, gratuitement et en quelques clics, partout en
              Algérie. Que vous cherchiez un généraliste, un spécialiste ou une clinique près de
              chez vous, dans les 69 wilayas, notre annuaire médical vous met en relation
              directement avec des praticiens vérifiés, disponibles 24h/24.
            </p>
          </div>

          {specialties.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-[#0F172A] mb-5 text-center">
                Trouvez un médecin par spécialité
              </h3>
              <div className="flex flex-wrap justify-center gap-2">
                {specialties.map((s) => (
                  <Link
                    key={s.id}
                    to={`/search?specialty=${s.id}`}
                    className="inline-flex items-center px-4 py-2 bg-[#F8FAFC] border border-[#E2E8F0] rounded-full text-sm text-[#0F172A] hover:border-[#0596DE] hover:text-[#0596DE] transition-all"
                  >
                    {s.name_fr}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Téléconsultation */}
      <section className="py-20 bg-[#F0F9FF]" data-testid="home-teleconsultation-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[#0596DE]/10 text-[#0596DE] text-sm font-semibold mb-4">
                <Video className="w-4 h-4" />
                {t('home_video_badge')}
              </span>
              <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">
                {t('home_video_title')}
              </h2>
              <p className="text-[#64748B] mt-4 leading-relaxed">
                {t('home_video_desc')}
              </p>
              <Link
                to="/search?video=true"
                className="inline-flex items-center gap-2 mt-6 px-6 py-3 rounded-full bg-[#0596DE] hover:bg-[#047CBA] text-white font-semibold transition-colors"
                data-testid="home-video-cta"
              >
                {t('home_video_cta')}
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-4">
              {[
                { icon: Clock, title: t('home_video_benefit1_title'), desc: t('home_video_benefit1_desc') },
                { icon: Shield, title: t('home_video_benefit2_title'), desc: t('home_video_benefit2_desc') },
                { icon: Phone, title: t('home_video_benefit3_title'), desc: t('home_video_benefit3_desc') },
              ].map(({ icon: Icon, title, desc }, i) => (
                <div key={i} className="flex items-start gap-4 p-4 rounded-2xl bg-white border border-[#E2E8F0]">
                  <div className="w-12 h-12 rounded-xl bg-[#E6F4FB] flex items-center justify-center flex-shrink-0">
                    <Icon className="w-6 h-6 text-[#0596DE]" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-[#0F172A] mb-1">{title}</h3>
                    <p className="text-sm text-[#64748B]">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Outils santé */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="home-health-tools-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0F172A]">Outils santé gratuits</h2>
            <p className="text-[#64748B] mt-3 max-w-xl mx-auto">
              Des calculateurs pratiques pour mieux comprendre votre santé, sans inscription.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {HEALTH_TOOLS.map((tool, i) => (
              <Link
                key={i}
                to={tool.to}
                data-testid={`home-health-tool-${i}`}
                className="bg-white rounded-2xl p-6 border border-[#E2E8F0] hover:shadow-md hover:border-[#0596DE]/30 transition-all flex flex-col"
              >
                <div className="w-12 h-12 rounded-xl bg-[#E6F4FB] flex items-center justify-center mb-4">
                  {tool.icon}
                </div>
                <h3 className="font-semibold text-[#0F172A] mb-1">{tool.title}</h3>
                <p className="text-sm text-[#64748B] leading-relaxed">{tool.desc}</p>
              </Link>
            ))}
          </div>
          <div className="text-center mt-10">
            <Link
              to="/outils-sante"
              className="inline-flex items-center gap-2 text-[#0596DE] font-semibold hover:underline"
            >
              Voir tous les outils santé
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Section Blog */}
      {blogArticles.length > 0 && (
        <section className="py-20 bg-[#020B18]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-10">
              <div>
                <h2 className="text-3xl font-bold text-white">Blog Santé</h2>
                <p className="text-white/50 mt-2">Conseils médicaux pour les Algériens</p>
              </div>
              <Link to="/blog" className="text-[#0596DE] hover:underline font-medium flex items-center gap-1 hidden md:flex">
                Voir tous les articles <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {blogArticles.map(article => (
                <Link key={article.article_id} to={`/blog/${article.slug}`}
                  className="bg-white/5 backdrop-blur-sm rounded-2xl hover:bg-white/10 transition-all overflow-hidden border border-white/10 hover:border-[#0596DE]/40 group">
                  <div className="w-full h-44 bg-gradient-to-br from-[#0596DE] to-[#0369A1] flex items-center justify-center">
                    {article.cover_url
                      ? <img src={article.cover_url} alt={article.title} className="w-full h-full object-cover" loading="lazy" />
                      : <span className="text-white text-5xl">🏥</span>
                    }
                  </div>
                  <div className="p-5">
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(article.tags || []).slice(0, 2).map(tag => (
                        <span key={tag} className="bg-[#0596DE]/15 text-[#0596DE] text-xs px-2 py-0.5 rounded-full border border-[#0596DE]/20">{tag}</span>
                      ))}
                    </div>
                    <h3 className="font-semibold text-white text-base mb-2 line-clamp-2 group-hover:text-[#0596DE] transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-white/50 text-sm line-clamp-2">{article.excerpt}</p>
                    <div className="mt-4 text-xs text-white/30">
                      {new Date(article.created_at).toLocaleDateString("fr-DZ")}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[#0596DE]" />
        <div className="absolute inset-0 bg-[url('data:image/svg+xml,%3Csvg%20width%3D%2220%22%20height%3D%2220%22%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%3E%3Ccircle%20cx%3D%221%22%20cy%3D%221%22%20r%3D%221%22%20fill%3D%22rgba(255%2C255%2C255%2C0.05)%22%2F%3E%3C%2Fsvg%3E')] opacity-50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-10">
            <div className="text-white max-w-lg">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">{t('cta_doctor_title')}</h2>
              <p className="text-white/80 text-lg leading-relaxed mb-2">
                {t('cta_doctor_subtitle')}
              </p>
              <ul className="space-y-2 text-white/70 text-sm mt-4">
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-white/90" />{t('cta_free')}</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-white/90" />{t('cta_online')}</li>
                <li className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-white/90" />{t('cta_visibility')}</li>
              </ul>
              <div className="mt-5 inline-flex items-center gap-3 bg-white/10 border border-white/20 rounded-2xl px-4 py-3" data-testid="cta-doctor-teleconsultation">
                <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <Video className="w-5 h-5 text-white" />
                </div>
                <p className="text-white text-sm font-medium">{t('cta_teleconsultation')}</p>
              </div>
            </div>
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-3">
              <Link to="/doctor/register">
                <Button
                  size="lg"
                  className="rounded-full bg-white text-[#0596DE] hover:bg-gray-50 px-8 h-14 text-base font-semibold shadow-xl hover:shadow-2xl transition-all w-full sm:w-auto"
                  data-testid="doctor-register-cta"
                >
                  <Users className="w-5 h-5 mr-2" />
                  {t('cta_btn')}
                </Button>
              </Link>
              <Link to="/clinic/register">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0596DE] px-8 h-14 text-base font-semibold shadow-xl transition-all w-full sm:w-auto"
                  data-testid="clinic-register-cta"
                >
                  Inscrire ma clinique
                </Button>
              </Link>
              <Link to="/hopital/inscription">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0596DE] px-8 h-14 text-base font-semibold shadow-xl transition-all w-full sm:w-auto"
                  data-testid="hospital-register-cta"
                >
                  Inscrire l'hopital
                </Button>
              </Link>
              <Link to="/pharmacie">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0596DE] px-8 h-14 text-base font-semibold shadow-xl transition-all w-full sm:w-auto"
                  data-testid="pharmacy-register-cta"
                >
                  Je suis pharmacien
                </Button>
              </Link>
              <Link to="/laboratoire/inscription">
                <Button
                  size="lg"
                  variant="outline"
                  className="rounded-full bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#0596DE] px-8 h-14 text-base font-semibold shadow-xl transition-all w-full sm:w-auto"
                  data-testid="laboratory-register-cta"
                >
                  Inscrire mon laboratoire
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-lg font-semibold text-[#0F172A] mb-6 text-center">
              Questions fréquentes
            </h3>
            <div className="space-y-5">
              <div>
                <p className="font-semibold text-[#0F172A]">Comment prendre rendez-vous en ligne avec un médecin en Algérie ?</p>
                <p className="text-[#64748B] text-sm mt-1 leading-relaxed">Utilisez la barre de recherche en haut de page pour choisir une spécialité et une wilaya, consultez les profils des médecins disponibles, puis réservez votre créneau en quelques clics.</p>
              </div>
              <div>
                <p className="font-semibold text-[#0F172A]">La prise de rendez-vous sur Tabbibak est-elle gratuite ?</p>
                <p className="text-[#64748B] text-sm mt-1 leading-relaxed">Oui, la recherche d'un médecin et la prise de rendez-vous sont entièrement gratuites pour les patients, sans aucun frais caché.</p>
              </div>
              <div>
                <p className="font-semibold text-[#0F172A]">Tabbibak couvre-t-il toutes les wilayas d'Algérie ?</p>
                <p className="text-[#64748B] text-sm mt-1 leading-relaxed">Oui, Tabbibak référence des médecins dans les 69 wilayas d'Algérie, dans toutes les spécialités.</p>
              </div>
              <div>
                <p className="font-semibold text-[#0F172A]">Puis-je consulter les avis d'autres patients avant de prendre rendez-vous ?</p>
                <p className="text-[#64748B] text-sm mt-1 leading-relaxed">Oui, chaque profil de médecin affiche les notes et avis laissés par les patients qui ont déjà consulté.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0F172A] text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-5 gap-10">
            <div>
              <span className="font-bold text-2xl font-['Outfit'] text-[#0596DE]">Tabbibak.dz</span>
              <p className="text-gray-400 text-sm mt-3 leading-relaxed">
                {t('footer_desc')}
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm tracking-wider uppercase text-gray-300">{t('footer_patients')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><Link to="/search" className="hover:text-white transition-colors">{t('footer_find')}</Link></li>
                <li><Link to="/pharmacies" className="hover:text-white transition-colors">Trouver une pharmacie</Link></li>
                <li><Link to="/outils-sante" className="hover:text-white transition-colors">Outils santé</Link></li>
                <li><Link to="/register" className="hover:text-white transition-colors">{t('footer_create_account')}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t('footer_login')}</Link></li>
                <li><Link to="/blog" className="hover:text-white transition-colors">Blog Santé</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm tracking-wider uppercase text-gray-300">{t('footer_doctors')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><Link to="/doctor/register" className="hover:text-white transition-colors">{t('footer_register')}</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">{t('footer_access')}</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm tracking-wider uppercase text-gray-300">{t('footer_contact')}</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><Link to="/pharmacie" className="hover:text-white transition-colors">Pharmaciens</Link></li>
                <li><Link to="/partenariat" className="hover:text-white transition-colors">Partenariats</Link></li>
                <li>contact@tabbibak.com</li>
                <li>{isRTL ? 'تيزي وزو و وهران' : 'Tizi Ouzou & Oran, Algérie'}</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4 text-sm tracking-wider uppercase text-gray-300">Informations utiles</h4>
              <ul className="space-y-3 text-gray-400 text-sm">
                <li><a href="https://crom-tiziouzou.dz/code" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Ordre National des Médecins</a></li>
                <li><a href="https://www.snapo-dz.net/site/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Ordre des Pharmaciens</a></li>
                <li><a href="https://cnas.dz/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Assurance Maladie (CNAS)</a></li>
                <li><a href="https://www.vidal.fr/" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">Vidal médical</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-500 text-sm">
            © {new Date().getFullYear()} Tabbibak.dz - {t('footer_rights')}
{' · '}
<Link to="/mentions-legales" className="hover:text-white transition-colors">Mentions légales & CGU</Link>
{' · '}
<Link to="/confidentialite" className="hover:text-white transition-colors">Politique de confidentialité</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
